import type { BarcodeRecord, KbBag, KbDay, KbDenom, KbOfficeFields, KbSaveBody, KbSource, KbStatus } from './kassenbuchApi.ts'

export const eur = (value: string | number | null | undefined) =>
  value == null || value === '' || !Number.isFinite(Number(value)) ? '–'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value))
export const eurSigned = (value: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', signDisplay: 'exceptZero' }).format(value)

export function isoToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now)
  const part = (type: string) => parts.find(p => p.type === type)?.value
  return `${part('year')}-${part('month')}-${part('day')}`
}
export function formatDateDE(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(iso)
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '–'
}
export function formatTimestampDE(value: string | null | undefined): string {
  if (!value) return '–'
  const date = new Date(value)
  return Number.isNaN(date.valueOf()) ? '–' : new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date)
}

/** Missing/mixed provenance stays unknown; p: is storage, not source. */
export function sourcesOf(record: KbOfficeFields): KbSource[] {
  return [...new Set([record.quelle, ...(record.records ?? []).flatMap(sourcesOf)].filter((s): s is KbSource => s === 'fm' || s === 'portal'))]
}
export const counted = (record: { gezaehlt?: string | null; umsatz?: string | null }) => record.gezaehlt ?? record.umsatz
/** Aggregates may return zero with no coffee mode even when no coffee line exists. */
export function hasCoffee(record: KbOfficeFields): boolean {
  return record.kaffee != null && record.kaffee !== '' && Number.isFinite(Number(record.kaffee))
    && (Number(record.kaffee) !== 0 || record.kaffeeImGezaehlten != null)
}
export function totalCents(count: number, coffee: number | null, included: boolean | null): number {
  return count + (included === false ? coffee ?? 0 : 0)
}
export function parseMoney(value: string): number | null {
  if (!/^-?\d+(?:[.,]\d{1,2})?$/.test(value.trim())) return null
  const cents = Math.round(Number(value.replace(',', '.')) * 100)
  return Number.isSafeInteger(cents) && cents >= -2147483648 && cents <= 2147483647 ? cents : null
}

/** Deduplicate explicit heads only. Repeated barcodes never establish membership. */
export function bagGroups(groups: readonly KbBag[] = [], records: readonly BarcodeRecord[] = []): KbBag[] {
  const byHead = new Map<string, KbBag>()
  for (const group of [...groups, ...records.flatMap(r => r.tuete ? [{ ...r.tuete, schule: r.schule, firma: r.firma }] : [])]) {
    const key = `${group.school_id}:${group.headId}`
    if (!byHead.has(key)) byHead.set(key, { ...group, tage: [...group.tage].sort((a, b) => a.datum.localeCompare(b.datum) || a.recordId.localeCompare(b.recordId)) })
  }
  return [...byHead.values()]
}
export function bagControlBody(bag: KbBag, kontrolliert: 'Ja' | 'Nein' = 'Ja'): KbSaveBody | null {
  const head = bag.tage.find(day => day.recordId === bag.headRecordId)
  if (!head?.modId || !bag.tage.length || bag.tage.some(day => !day.recordId || !day.modId)
    || new Set(bag.tage.map(day => day.recordId)).size !== bag.tage.length) return null
  return { recordId: head.recordId, modId: head.modId, kontrolliert, members: bag.tage.map(({ recordId, modId }) => ({ recordId, modId })) }
}

type ControlRecord = KbOfficeFields & { recordId?: string | null; modId?: string | null; speicherModus?: KbSource }
/** Collect every sheet before writing; a bag is always one complete atomic request. */
export function bulkControlBodies(rows: readonly ControlRecord[], kontrolliert: 'Ja' | 'Nein' = 'Ja'): KbSaveBody[] | null {
  const sheets = rows.flatMap<ControlRecord>(row => row.records?.length ? row.records : [row])
  const groups = new Map<string, KbSaveBody>()
  const versions = new Map<string, string>()
  const covered = new Set<string>()
  const remember = (recordId: string | null | undefined, modId: string | null | undefined) => {
    if (!recordId || !modId || (versions.has(recordId) && versions.get(recordId) !== modId)) return false
    versions.set(recordId, modId)
    return true
  }
  for (const sheet of sheets) {
    if (!remember(sheet.recordId, sheet.modId)) return null
    if (sheet.sammel_tag_id != null && !sheet.tuete) return null
    if (sheet.tuete && !sheet.tuete.tage.some(member => member.recordId === sheet.recordId)) return null
  }
  const addGroup = (key: string, body: KbSaveBody) => {
    const previous = groups.get(key)
    if (previous && (previous.recordId !== body.recordId || previous.members!.length !== body.members!.length
      || body.members!.some(m => !previous.members!.some(p => p.recordId === m.recordId && p.modId === m.modId)))) return false
    if (!previous && body.members!.some(m => covered.has(m.recordId))) return false
    for (const member of body.members!) {
      if (!remember(member.recordId, member.modId)) return false
      covered.add(member.recordId)
    }
    groups.set(key, body)
    return true
  }
  const dayMembers = new Set<string>()
  for (const row of rows) {
    // Standalone correction rows (including bags of one day) share the API's atomic day scope.
    if (row.speicherModus !== 'portal' || !row.records || row.records.length < 2
      || row.records.some(sheet => sheet.sammel_tag_id != null || (sheet.tuete?.tage.length ?? 0) > 1)) continue
    const first = row.records[0]
    if (first.school_id == null || !first.date || row.records.some(sheet => String(sheet.school_id) !== String(first.school_id) || sheet.date !== first.date)) return null
    const members = row.records.map(sheet => ({ recordId: sheet.recordId!, modId: sheet.modId! }))
    const unique = [...new Map(members.map(member => [member.recordId, member])).values()]
    const body = { ...unique[0], kontrolliert, members: unique }
    if (!addGroup(`day:${first.school_id}:${first.date}`, body)) return null
    for (const member of members) dayMembers.add(member.recordId)
  }
  for (const row of [...rows, ...sheets]) {
    if (!row.tuete) continue
    const body = bagControlBody(row.tuete, kontrolliert)
    if (!body) return null
    if (body.members!.length === 1 && dayMembers.has(body.recordId)) {
      if (!remember(body.recordId, body.modId)) return null
      continue
    }
    if (!addGroup(`bag:${row.tuete.school_id}:${row.tuete.headId}`, body)) return null
  }
  const bodies = [...groups.values()]
  for (const [recordId, modId] of versions) {
    if (!covered.has(recordId)) bodies.push({ recordId, modId, kontrolliert })
  }
  return bodies
}

/** Control/reopen the complete portal day, or its bag, using all current row versions. */
export function dayControlBody(record: KbDay, kontrolliert: 'Ja' | 'Nein' = 'Ja'): KbSaveBody | null {
  if (record.tuete && (record.tuete.tage.length !== 1 || record.sammel_tag_id != null)) return bagControlBody(record.tuete, kontrolliert)
  if (!record.recordId || !record.modId) return null
  const body: KbSaveBody = { recordId: record.recordId, modId: record.modId, kontrolliert }
  if (record.speicherModus !== 'portal') return body
  if (record.records?.length && (record.records.some(sheet => sheet.date !== record.date || String(sheet.school_id) !== String(record.school_id))
    || !record.records.some(sheet => sheet.recordId === record.recordId && sheet.modId === record.modId))) return null
  const bodies = bulkControlBodies([record], kontrolliert)
  if (bodies?.length !== 1) return null
  return { ...bodies[0], ...body }
}

/** Storage mode is independent of provenance: imported FM rows are editable in the portal. */
export const canCorrectPortalAmounts = (record: { speicherModus?: KbSource }) => record.speicherModus === 'portal'

export interface CorrectionDraft { counts: Record<string, string>; kassenbestand: string; gezaehlt: string; kaffee: string; included: boolean | null }
export function seedCorrection(record: KbDay): CorrectionDraft {
  return { counts: Object.fromEntries((record.denoms ?? []).map(d => [d.field, String(d.count)])),
    kassenbestand: record.kassenbestand ?? '', gezaehlt: counted(record) ?? '',
    kaffee: record.kaffee ?? '', included: record.kaffeeImGezaehlten ?? null }
}
/** Only changed fields are sent. FM's independent counted amount/subtotals survive. */
export function correctionBody(record: KbDay, draft: CorrectionDraft): KbSaveBody | null {
  if (!record.recordId) return null
  const body: KbSaveBody = { recordId: record.recordId, modId: record.modId }
  const initial = seedCorrection(record)
  if (!canCorrectPortalAmounts(record) && (draft.gezaehlt !== initial.gezaehlt || draft.kaffee !== initial.kaffee || draft.included !== initial.included)) return null
  const fm = record.quelle !== 'portal'
  const counts: Record<string, number> = {}
  for (const denom of record.denoms ?? []) {
    const raw = draft.counts[denom.field] ?? String(denom.count)
    if (raw === String(denom.count)) continue
    if (!/^-?\d+$/.test(raw)) return null
    const value = Number(raw)
    if (!Number.isInteger(value) || value < (fm ? -32768 : 0) || value > (fm ? 32767 : 9999)) return null
    counts[denom.field] = value
  }
  if (Object.keys(counts).length) body.counts = counts
  if (draft.kassenbestand !== initial.kassenbestand) {
    const cents = parseMoney(draft.kassenbestand)
    if (cents == null || (!fm && cents < 0)) return null
    body.kassenbestand = cents / 100
  }
  if (fm && draft.gezaehlt !== initial.gezaehlt) {
    const cents = parseMoney(draft.gezaehlt)
    if (cents == null) return null
    body.gezaehlt_cents = cents
  }
  if (draft.kaffee !== initial.kaffee || draft.included !== initial.included) {
    const coffee = draft.kaffee.trim() === '' ? null : parseMoney(draft.kaffee)
    if (draft.kaffee.trim() !== '' && (coffee == null || coffee < 0 || draft.included == null)) return null
    body.kaffee_cents = coffee
    body.kaffee_im_gezaehlten = coffee == null ? null : draft.included
  }
  return body
}
export function kbErrorKey(error: unknown): string {
  const e = error as { code?: string; status?: number } | null
  if (e?.code === 'NUR_IM_PORTAL') return 'office.portalCorrectionHint'
  if (e?.code === 'VERALTET' || (e?.status === 409 && !e.code)) return 'office.stale'
  if (e?.code === 'MEHRERE_EINTRAEGE') return 'office.multipleEntries'
  if (e?.code?.includes('TUETE_UNVOLLSTAENDIG')) return 'office.incompleteBag'
  if (e?.code === 'KASSENBUCH_IMPORT_UNVOLLSTAENDIG') return 'office.importIncomplete'
  if (e?.status === 503 || e?.code?.startsWith('KASSENBUCH_')) return 'office.unavailable'
  return 'office.loadFailed'
}

/** Control-date reports may have several sheets in one calendar cell. */
export function groupReportDays<T extends KbOfficeFields & {
  datum: string | null; kontrolliert: boolean; umsatz: string; sapBar: string | null
  kassenbestand?: string | null; scheine?: string | null; muenzen?: string | null
  bei?: string | null; am?: string | null; person?: string | null; diff?: string | null
}>(days: readonly T[]): T[] {
  const groups = new Map<string | null, T[]>()
  for (const day of days) groups.set(day.datum, [...(groups.get(day.datum) ?? []), day])
  return [...groups.values()].map(rows => {
    if (rows.length === 1) return rows[0]
    const sum = (key: 'umsatz' | 'gezaehlt' | 'kaffee' | 'gesamtumsatz' | 'kassenbestand' | 'scheine' | 'muenzen') => rows.every(row => row[key] != null)
      ? (rows.reduce((total, row) => total + Math.round(Number(row[key]) * 100), 0) / 100).toFixed(2) : null
    const first = rows[0]
    const same = <K extends keyof T>(key: K) => rows.every(row => row[key] === first[key]) ? first[key] : null
    const umsatz = sum('umsatz')!
    const sapBar = same('sapBar')
    return { ...first, umsatz, gezaehlt: sum('gezaehlt'), kaffee: sum('kaffee'), gesamtumsatz: sum('gesamtumsatz'),
      kassenbestand: sum('kassenbestand'), scheine: sum('scheine'), muenzen: sum('muenzen'),
      quelle: same('quelle'), kaffeeImGezaehlten: same('kaffeeImGezaehlten'),
      kontrolliert: rows.every(row => row.kontrolliert), bei: same('bei'), am: same('am'), person: same('person'),
      records: rows.flatMap(row => row.records ?? []), sapBar,
      diff: sapBar == null ? null : (Number(umsatz) - Number(sapBar)).toFixed(2) }
  })
}

export interface BagSums {
  /** Days (sheets) summed. */
  count: number
  /** Counted money (Umsatz): sum of counted(sheet) over the bag's own sheets. */
  umsatz: number
  gezaehlt: number | null
  kassenbestand: number
  kaffee: number | null
  gesamtumsatz: number | null
  scheine: number
  muenzen: number
  /** SAP Bar summed over DISTINCT school/day; null when no member has SAP data. */
  sapBar: number | null
  diff: number | null
  status: KbStatus
  tolerance: number
  denoms: KbDenom[]
}
const cents = (value: string | number | null | undefined) => Math.round(Number(value) * 100)
const known = (value: string | null | undefined) => value != null && value !== '' && Number.isFinite(Number(value))
const sumKnown = (days: readonly KbDay[], pick: (day: KbDay) => string | null | undefined): number | null =>
  days.length && days.every(day => known(pick(day))) ? days.reduce((sum, day) => sum + cents(pick(day)), 0) / 100 : null

/**
 * Cumulated view of one Sammeltüte (all members from the bag chain, P77/P85).
 * Ported from the pre-T6 cumulated view: sums per sheet, SAP Bar over DISTINCT school/day,
 * Differenz = Umsatz - SAP. Umsatz is counted money (Kassenbestand is never subtracted, k_umsatz a).
 */
export function cumulateBag(days: readonly (KbDay | null | undefined)[]): BagSums | null {
  const loaded = days.filter((day): day is KbDay => day != null && day.exists !== false)
  if (!loaded.length) return null
  const seen = new Set<string>()
  let umsatzCents = 0, sapCents = 0, hasSap = false
  for (const day of loaded) {
    const key = `${day.school_id ?? '?'}::${day.date}`
    // Counted money is the bag's own sheets only (a day may also hold a sheet of another bag).
    umsatzCents += cents(counted(day) ?? 0)
    if (seen.has(key)) continue
    seen.add(key)
    if (known(day.sapBar)) { sapCents += cents(day.sapBar); hasSap = true }
  }
  const byField = new Map<string, KbDenom>()
  for (const day of loaded) for (const denom of day.denoms ?? []) {
    const existing = byField.get(denom.field)
    const count = (existing?.count ?? 0) + denom.count
    byField.set(denom.field, { ...(existing ?? denom), count, summe: (denom.value * count).toFixed(2) })
  }
  const denoms = [...byField.values()]
  const kindSum = (day: KbDay, kind: 'note' | 'coin', total: string | null | undefined) => known(total) ? cents(total)
    : (day.denoms ?? []).filter(d => d.kind === kind).reduce((sum, d) => sum + Math.round(d.value * 100) * d.count, 0)
  const tolerance = Number(loaded[0].tolerance ?? 10)
  const umsatz = umsatzCents / 100
  const sapBar = hasSap ? sapCents / 100 : null
  const diff = sapBar == null ? null : (umsatzCents - sapCents) / 100
  const status: KbStatus = diff == null ? 'no_sap' : Math.abs(diff) < 0.01 ? 'match' : Math.abs(diff) <= tolerance ? 'minor' : 'flagged'
  return {
    count: loaded.length, umsatz, gezaehlt: sumKnown(loaded, counted),
    kassenbestand: loaded.reduce((sum, day) => sum + cents(day.kassenbestand ?? 0), 0) / 100,
    kaffee: sumKnown(loaded, day => day.kaffee), gesamtumsatz: sumKnown(loaded, day => day.gesamtumsatz),
    scheine: loaded.reduce((sum, day) => sum + kindSum(day, 'note', day.scheinGesamt), 0) / 100,
    muenzen: loaded.reduce((sum, day) => sum + kindSum(day, 'coin', day.munzeGesamt), 0) / 100,
    sapBar, diff, status, tolerance, denoms,
  }
}
